// The Blocks sheet: one row per block DEFINITION, and columns you add yourself.
//
// The difference from Features and Usages is where the columns come from. There
// the project already has the fields and the table discovers them. Here you make
// them up: type "Standard rent" in a free header, fill the column down, and every
// placement of that block carries it.
//
// On the TYPE, not the instance — that is the whole point. A standard rent is a
// fact about a floorplan, not about the one on level 6.
//
// The values go RAW onto the definition's `properties`, at the top level. The
// whole point of putting a value on the block is that Giraffe's own Edit
// Properties panel, its filters and its exports can read it, and a value under
// this app's own namespace is a value only this app can see. The read-merge
// that keeps the rest of the definition intact lives in the adapter, next to
// the merge rule it works around.

import type { Sheet, Workbook } from '@/model/types';
import { naturalCompare } from '@/model/collate';
import { createSheet, getCell, newId, sheetByRole, uniqueSheetName } from '@/model/workbook';
import type { BlockDef } from '@/giraffe/adapter';
import { prettyField } from '@/data/classify';
import {
  FIRST_DATA_ROW,
  columnsOf,
  tableDiff,
  writeTable,
  type EntityDiff,
  type EntityRow,
  type TableSpec,
} from './entityTable';

export const BLOCKS_SHEET_NAME = 'Blocks';

/** Read from the definition, never written back — it says what the block IS. */
const READ_ONLY = new Set(['id', 'instances', 'floorPlate']);

/** Instances first — the one number that makes the list navigable — then every other column A-Z. */
function blockColumnSort(fields: string[]): string[] {
  const remaining = new Set(fields);
  const pinned = ['instances'].filter((f) => remaining.delete(f));
  const rest = [...remaining].sort((a, b) => naturalCompare(a, b));
  return [...pinned, ...rest];
}

const SPEC: TableSpec = {
  label: 'Block',
  labelFields: new Set(['name']),
  order: [],
  columnSort: blockColumnSort,
  readOnly: READ_ONLY,
  /*
   * No units, which here means "not converted and not labelled".
   *
   * These columns are invented by the user — the header IS the property name —
   * so the app has no basis for calling one an area or a rate, and a number
   * typed onto a block definition goes back to it verbatim. Everything the
   * PROJECT computes is on the other two sheets, where it is converted and
   * labelled.
   */
  units: null,
  rawHeaders: true,
};

export function blockLabel(block: BlockDef): string {
  const name = block.name;
  return typeof name === 'string' && name.trim() !== '' ? name.trim() : `Block ${block.id.slice(-4)}`;
}

/**
 * A definition as a row: what the block is, then the columns someone added.
 *
 * `instances` is how many times it is placed, which is the one number that makes
 * the list navigable — a definition placed nowhere is a definition to delete,
 * and one placed forty times is worth getting right.
 */
export function blockRows(blocks: Record<string, BlockDef>, instanceCounts: ReadonlyMap<string, number>): EntityRow[] {
  return Object.values(blocks)
    .map((block) => ({
      name: blockLabel(block),
      // Every property the definition carries, at the top level where Giraffe's
      // own panel shows them — plus the instance count, which is not a property
      // but is the one number that makes the list navigable.
      fields: { instances: instanceCounts.get(block.id) ?? 0, ...(block.properties ?? {}) },
    }))
    .sort((a, b) => naturalCompare(a.name, b.name));
}

/** Block id by row label, so a sheet row can be written back to its definition. */
export function blockIds(blocks: Record<string, BlockDef>): Map<string, string> {
  const out = new Map<string, string>();
  for (const block of Object.values(blocks)) out.set(blockLabel(block).toLowerCase(), block.id);
  return out;
}

/** How many features place each block, counted off the raw sections. */
export function countInstances(rows: readonly { properties: Record<string, unknown> }[]): Map<string, number> {
  const out = new Map<string, number>();
  for (const row of rows) {
    const id = row.properties.blockId;
    if (typeof id !== 'string' || id === '') continue;
    out.set(id, (out.get(id) ?? 0) + 1);
  }
  return out;
}

export function blockColumns(rows: readonly EntityRow[]): string[] {
  return columnsOf(rows, SPEC);
}

export function ensureBlocksSheet(wb: Workbook): Sheet {
  let sheet = sheetByRole(wb, 'blocks');
  if (!sheet) {
    sheet = createSheet(uniqueSheetName(wb, BLOCKS_SHEET_NAME), newId('blocks'));
    sheet.role = 'blocks';
    sheet.colWidths[1] = 190;
    wb.sheets.push(sheet);
  }
  return sheet;
}

export function writeBlocks(sheet: Sheet, rows: readonly EntityRow[], preserveEdits = true, wb?: Workbook): void {
  writeTable(sheet, rows, SPEC, preserveEdits, wb);
}

export function blockDiff(sheet: Sheet, rows: readonly EntityRow[]): EntityDiff {
  return tableDiff(sheet, rows, SPEC);
}

/**
 * Header text to the field it means.
 *
 * A header reads `Floor Plate` and the property is `floorPlate`, so both spellings
 * have to resolve to the one field. Without this the pretty header looks like a
 * column nobody has seen before, and the write invents a SECOND property beside
 * the real one — `"Floor Plate": true` sitting next to `floorPlate`.
 */
function headerFields(rows: readonly EntityRow[]): Map<string, string> {
  const out = new Map<string, string>();
  for (const field of blockColumns(rows)) {
    out.set(field.toLowerCase(), field);
    out.set(prettyField(field).toLowerCase(), field);
  }
  return out;
}

/** Every header on the sheet, by the column it sits in. */
function headersOf(sheet: Sheet): { text: string; col: number }[] {
  const out: { text: string; col: number }[] = [];
  for (let c = 2; c <= Math.max(sheet.cols, 40); c++) {
    const header = getCell(sheet, 1, c)?.v;
    if (header === undefined || header === null) continue;
    const text = String(header).trim();
    if (text !== '') out.push({ text, col: c });
  }
  return out;
}

/**
 * The columns someone ADDED — the headers this table does not already know.
 *
 * This is what makes the sheet a place to INVENT a field rather than only to
 * edit the ones that exist: type a header, fill the column down, and every
 * placement of that block carries it.
 */
export function addedColumns(sheet: Sheet, rows: readonly EntityRow[]): { field: string; col: number }[] {
  const known = headerFields(rows);
  const label = SPEC.label.toLowerCase();
  return headersOf(sheet)
    .filter((h) => h.text.toLowerCase() !== label && !known.has(h.text.toLowerCase()))
    .map((h) => ({ field: h.text, col: h.col }));
}

/**
 * Everything the sheet says about each block, ready for the definitions.
 *
 * `managed` is every column the sheet controls, filled in or not: a value
 * cleared out of a column has to reach the write as an ABSENCE, and collecting
 * only what is filled in is how a deletion gets lost.
 */
export function blockValues(
  sheet: Sheet,
  rows: readonly EntityRow[],
  blocks: Record<string, BlockDef>
): { values: Map<string, Record<string, unknown>>; managed: string[] } {
  const ids = blockIds(blocks);
  const known = headerFields(rows);
  const label = SPEC.label.toLowerCase();

  /** Column -> the property name a value there is written under. */
  const at: { field: string; col: number }[] = [];
  for (const header of headersOf(sheet)) {
    if (header.text.toLowerCase() === label) continue;
    const field = known.get(header.text.toLowerCase()) ?? header.text;
    // `instances` is a count, `id` is the address: read, never written.
    if (READ_ONLY.has(field)) continue;
    at.push({ field, col: header.col });
  }

  const values = new Map<string, Record<string, unknown>>();
  const last = FIRST_DATA_ROW + Object.keys(blocks).length + 40;
  for (let r = FIRST_DATA_ROW; r <= last; r++) {
    const label2 = getCell(sheet, r, 1)?.v;
    if (label2 === undefined || label2 === null) continue;
    const id = ids.get(String(label2).trim().toLowerCase());
    if (id === undefined) continue;
    const own: Record<string, unknown> = {};
    for (const { field, col } of at) {
      const cell = getCell(sheet, r, col);
      // A formula in one of these cells is the user's spreadsheet, not a value
      // to push into the project.
      if (cell?.f !== undefined) continue;
      const v = cell?.v;
      if (v === undefined || v === null || v === '') continue;
      own[field] = v;
    }
    values.set(id, own);
  }
  return { values, managed: at.map((a) => a.field) };
}
